// frontend/src/components/TaskRecipeView.js
import React, { useState } from 'react';
import './TaskRecipeView.css';
import api from '../api';
import MutantStepNode from './MutantStepNode';
import ImageLightbox from './ImageLightbox';
import { useNotification } from '../context/NotificationContext';
import ReadableText from './ReadableText';

export default function TaskRecipeView({
    recipe,
    onToggleSubstep,
    isDraft,
    taskId,
    onImageGenerated,
    onGenerateSubsteps, // New prop for manual generation
    backgroundJobs = {},
    onBackgroundJobStarted
}) {
  // --- HOOKS MUST BE UNCONDITIONAL ---
  const { addNotification } = useNotification();
  const [expandedImage, setExpandedImage] = useState(null);
  const [loadingImages, setLoadingImages] = useState({}); // { [stepIndex]: boolean }
  const [loadingSubsteps, setLoadingSubsteps] = useState({}); // { [stepIndex]: boolean }

  // Local state to track "Show/Hide" toggle for steps that have images
  // We initialize based on whether the image exists, but user can toggle it off.
  const [visualsEnabled, setVisualsEnabled] = useState({});

  if (!recipe) return null;

  // --- HANDLER: Toggle Slider (Generate or Show/Hide) ---
  const handleToggleVisual = async (stepIndex, currentStepObj) => {
      // 1. If currently showing/enabled, turn it off.
      if (visualsEnabled[stepIndex] || (currentStepObj.imageUrl && visualsEnabled[stepIndex] !== false)) {
          setVisualsEnabled(prev => ({ ...prev, [stepIndex]: false }));
          return;
      }

      // 2. If turning ON:
      setVisualsEnabled(prev => ({ ...prev, [stepIndex]: true }));

      // 3. If image already exists, we are done (just showed it).
      if (currentStepObj.imageUrl) return;

      // 4. If image doesn't exist, GENERATE IT.
      if (isDraft || !taskId) {
          addNotification("Debes guardar la tarea antes de generar imágenes.", "info");
          setVisualsEnabled(prev => ({ ...prev, [stepIndex]: false })); // Revert
          return;
      }

      setLoadingImages(prev => ({ ...prev, [stepIndex]: true }));
      try {
          const res = await api.post(`/tareas/${taskId}/steps/${stepIndex}/image`);
          if (res.status === 202 && res.data?.jobId) {
              if (onBackgroundJobStarted) onBackgroundJobStarted(stepIndex, res.data.jobId);
          } else if (res.data.ok && onImageGenerated) {
              onImageGenerated(stepIndex, res.data.step);
          }
      } catch (err) {
          console.error("Error generando imagen para paso", stepIndex, err);
          addNotification("Error generando imagen: " + (err.response?.data?.error || err.message), "error");
          setVisualsEnabled(prev => ({ ...prev, [stepIndex]: false })); // Revert on error
      } finally {
          setLoadingImages(prev => ({ ...prev, [stepIndex]: false }));
      }
  };

  const handleManualSubsteps = async (stepIndex) => {
      if (loadingSubsteps[stepIndex]) return;
      if (isDraft || !taskId) return addNotification("Debes aceptar la tarea primero.", "info");

      setLoadingSubsteps(prev => ({ ...prev, [stepIndex]: true }));
      try {
          if (onGenerateSubsteps) {
              await onGenerateSubsteps(stepIndex);
          }
      } catch (e) {
          console.error("Error generating substeps", e);
      } finally {
          setLoadingSubsteps(prev => ({ ...prev, [stepIndex]: false }));
      }
  };

  // --- HELPER: Convertir texto plano a pasos si falla el JSON ---
  function parseStepsFromText(text) {
      if (!text || typeof text !== 'string') return [];
      const clean = text.replace(/\*\*/g, '').trim(); 
      
      const numberedRegex = /(?:^|\n)(\d+[.)]\s*)/;
      const bulletRegex = /(?:^|\n)(?:[-•*]\s+)/;

      if (numberedRegex.test(clean)) {
          const parts = clean.split(numberedRegex).filter(p => p.trim());
          const steps = [];
          for (let i = 0; i < parts.length; i += 2) {
              if (parts[i+1]) {
                 steps.push({ 
                     title: `Paso ${parts[i].replace(/[.)]/g, '')}`,
                     text: parts[i+1].trim(), 
                     completed: false, 
                     substeps: [] 
                 });
              }
          }
          if (steps.length > 0) return steps;
      } 
      else if (bulletRegex.test(clean)) {
          const parts = clean.split(bulletRegex).map(p => p.trim()).filter(Boolean);
          return parts.map((p, i) => ({
             title: `Paso ${i+1}`,
             text: p,
             completed: false,
             substeps: []
          }));
      }

      const paragraphs = clean.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
      return paragraphs.map((p, i) => ({
          title: `Paso ${i + 1}`,
          text: p,
          completed: false,
          substeps: []
      }));
  }

  function cleanTitle(title, index) {
      if (!title) return '';
      let cleaned = title.replace(new RegExp(`^Paso\\s*${index + 1}[:\\.]?\\s*`, 'i'), '').trim();
      cleaned = cleaned.replace(new RegExp(`^${index + 1}[:\\.]?\\s*`), '').trim();
      return cleaned;
  }

  let stepsToRender = [];
  
  if (Array.isArray(recipe.guide) && recipe.guide.length > 0) {
      stepsToRender = recipe.guide;
  } else if (Array.isArray(recipe.steps) && recipe.steps.length > 0) {
      stepsToRender = recipe.steps;
  } 
  else if (recipe.raw) {
      stepsToRender = parseStepsFromText(recipe.raw);
  }
  else if (typeof recipe.steps === 'string') {
      stepsToRender = parseStepsFromText(recipe.steps);
  }

  return (
    <div className="task-recipe-container">
      {/* Lightbox Modal */}
      {expandedImage && (
          <ImageLightbox
            src={expandedImage}
            alt="Expanded Step"
            onClose={() => setExpandedImage(null)}
          />
      )}

      {recipe.summary && stepsToRender.length > 0 && recipe.summary !== recipe.raw && (
          <div className="task-summary-box">
              <p><ReadableText text={recipe.summary} label="Leer resumen de tarea">{recipe.summary}</ReadableText></p>
          </div>
      )}

      <div className="steps-list">
        {stepsToRender.length === 0 && (
            <div className="loading-steps">
                {recipe.raw ? (
                    <div style={{whiteSpace: 'pre-wrap', color: '#ccc', padding: '10px', background: '#222', borderRadius: '4px'}}>
                        <ReadableText text={recipe.raw} label="Leer guía generada">
                            {recipe.raw}
                        </ReadableText>
                    </div>
                ) : (
                    <p>Generando pasos...</p>
                )}
            </div>
        )}
        
        {stepsToRender.map((stepRaw, idx) => {
          if (!stepRaw) return null;

          const stepObj = typeof stepRaw === 'string' 
              ? { text: stepRaw, completed: false, substeps: [] } 
              : (stepRaw || {});

          let title = stepObj.title || '';
          if (title) title = cleanTitle(title, idx);

          let rawText = stepObj.text || stepObj.content || '';
          let text = rawText.replace(new RegExp(`^Paso\\s*${idx + 1}[:\\.]?\\s*`, 'i'), '');
          text = text.replace(new RegExp(`^${idx + 1}[:\\.]?\\s*`), '');
          text = text.trim();
          if (!text && rawText) text = rawText;

          const isVisualActive = visualsEnabled[idx] === true || (stepObj.imageUrl && visualsEnabled[idx] !== false) || !!backgroundJobs[idx] || !!backgroundJobs[`${idx}`];

          return (
            <MutantStepNode
                key={idx}
                step={stepObj}
                index={idx}
                taskId={taskId}
                isDraft={isDraft}
                title={title}
                text={text}
                isVisualActive={isVisualActive}
                isLoading={loadingImages[idx]}
                isBackground={!!backgroundJobs[idx]}
                isSubstepsLoading={loadingSubsteps[idx]}
                onToggleSubstep={onToggleSubstep}
                onToggleComplete={(stepIdx) => onToggleSubstep && onToggleSubstep(stepIdx, -1)}
                onGenerateSubsteps={handleManualSubsteps}
                onToggleVisual={handleToggleVisual}
                onExpandImage={setExpandedImage}
                backgroundJobs={backgroundJobs}
                isLast={idx === stepsToRender.length - 1}
            />
          );
        })}
      </div>
    </div>
  );
}
